<!--
  canonical product editor ContextPanel — slot for the active LeftRail section.

  Plan: 02-product-editor.md (Day 1 (canonical product editor)).

  Day 1: switch case maps selectedLeftRailItem to the existing 5 panels.
  Reuses-as-is with light wrapper styling; full restyle pass during the
  Day 3 (we picked the 'Full restyle' answer to the audience
  question, so the panels get rail-native rebuilds across the 3 days).

  Day 2: when selectedLeftRailItem starts with 'placement:', mount the
  DecorationInspector instead of one of the setup panels.

  Day 6: the DecorationInspector now lives in the shell's right rail
  (3-col body when a placement is selected). When a placement is
  selected, ContextPanel renders nothing — the middle column shows
  only Stage + filmstrip + routing above; the right rail owns the
  inspector. When a Product Setup item is selected, ContextPanel
  routes to the matching panel as before.
-->
<template>
    <!--
      Day 6 gate round-1 P2.4: the outer <section> is gated by v-if
      on !isPlacementSelection so the empty placeholder section
      doesn't render when a placement is selected (it would otherwise
      occupy DOM and become a ghost-styling risk if anyone later
      added padding/border to .context-panel-root). The right rail
      owns the inspector; the middle column ends after ColorFilmstrip.
    -->
    <section
        v-if="!isPlacementSelection"
        data-testid="context-panel-root"
        class="context-panel-root"
    >
        <SupplierSkuPanel
            v-if="active === 'supplier-sku'"
            data-testid="context-panel-supplier-sku"
        />
        <!--
          Sizes & Colors merge (2026-07-14): the two setup panels stack in
          ONE scrollable tab -- SizesPanel on top, ColorsPanel below. The
          context column (.product-editor-context) already owns overflow-y,
          so this wrapper adds NO competing scroll. Co-mounting is safe
          because neither panel writes store.supplierMeta (the shell is the
          sole writer, #2003) -- there is no dual-writer race.
        -->
        <div
            v-else-if="active === 'sizes-colors'"
            data-testid="context-panel-sizes-colors"
            class="stacked-sections"
        >
            <SizesPanel />
            <ColorsPanel />
        </div>
        <PricingPanel
            v-else-if="active === 'pricing'"
            data-testid="context-panel-pricing"
        />
        <StorefrontListingPanel
            v-else-if="active === 'storefront'"
            data-testid="context-panel-storefront"
        />
        <div
            v-else
            data-testid="context-panel-empty"
            class="context-panel-empty"
        >
            <p>Pick a section in the rail.</p>
        </div>
    </section>
</template>

<script setup>
import { computed } from "vue";
import { useProductEditorUiState } from "@/stores/productEditorUiState";
import SupplierSkuPanel from "./sections/SupplierSkuPanel.vue";
import StorefrontListingPanel from "./sections/StorefrontListingPanel.vue";
import ColorsPanel from "./sections/ColorsPanel.vue";
import SizesPanel from "./sections/SizesPanel.vue";
import PricingPanel from "./sections/PricingPanel.vue";

const editorUi = useProductEditorUiState();

const active = computed(() => editorUi.selectedLeftRailItem);

const isPlacementSelection = computed(
    () => typeof active.value === "string" && active.value.startsWith("placement:"),
);
</script>

<style scoped>
.context-panel-root {
    width: 100%;
}

/* Sizes & Colors merge: stack SizesPanel above ColorsPanel with a gap.
   No overflow here -- the parent .product-editor-context owns scroll. */
.stacked-sections {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.context-panel-empty {
    color: var(--tf-text-muted, #6b7280);
    font-size: 14px;
    padding: 32px 0;
}
</style>
